import cn from 'clsx'
import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import CircleCheckboxInput from 'components/Luxkit/Checkbox/CircleCheckboxInput'
import FlightItemCard from 'tripPlanner/components/ItemCard/Flight/FlightItemCard'
import FlightItemCardSkeleton from 'tripPlanner/components/ItemCard/Flight/FlightItemCardSkeleton'
import TripContext, { TripContextValue } from 'tripPlanner/contexts/TripContext'
import { useAirport } from 'tripPlanner/hooks/api'
import { FlightSchedule } from 'tripPlanner/types/common'
import { FlightItem } from 'tripPlanner/types/tripItem'

interface Props {
  flight: FlightSchedule
  showSelect?: boolean
  selected?: boolean
  onClick: (flightKey: string) => void
}

const FlightCard = styled(FlightItemCard)`
  transition: border-color 0.2s;

  &.selected {
    border-color: ${({ theme }) => theme.palette.primary.normal};
  }
`

const defaultTripContext: TripContextValue = {
  tripId: '',
  viewAsTemplate: false,
  isPublicTrip: false,
  permissions: {
    canAdmin: false,
    canEdit: false,
    canPublish: false,
  },
}

const WithSelect = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

function FlightLookupTile({
  flight,
  showSelect,
  selected,
  onClick: onClickProp,
}: Props) {
  const { data: departureAirport, isLoading: departureAirportLoading } =
    useAirport({ code: flight.departureAirportCode })

  const { data: arrivalAirport, isLoading: arrivalAirportLoading } = useAirport(
    { code: flight.arrivalAirportCode },
  )

  const onClick = useCallback(
    () => onClickProp(flight.key),
    [onClickProp, flight.key],
  )

  // Take our lookup information and create an item from it to display with
  const flightItem = useMemo(
    (): FlightItem => ({
      isBooked: false,
      type: 'FLIGHT',
      tripId: '',
      confirmationItemCodes: [],
      id: '',
      createdAt: moment(),
      updatedAt: moment(),
      createdBy: '',
      updatedBy: '',
      sourceType: 'NA',
      originAirportCode: departureAirport?.airportCode ?? '',
      destinationAirportCode: arrivalAirport?.airportCode ?? '',
      startPlace: {
        name: departureAirport?.airportName ?? '',
        type: 'CUSTOM',
      },
      endPlace: {
        name: arrivalAirport?.airportName ?? '',
        type: 'CUSTOM',
      },
      startDate: flight.departureDate,
      startTime: flight.departureTime,
      startUtcDateTime: undefined,
      startTimezone: undefined,
      endDate: flight.arrivalDate,
      endTime: flight.arrivalTime,
      endUtcDateTime: undefined,
      endTimezone: undefined,
      airlineName: flight.airlineName,
      flightNumber: `${flight.airlineCode}${flight.flightNumber}`,
      sortableName: departureAirport?.airportName ?? '',
    }),
    [arrivalAirport, departureAirport, flight],
  )

  if (departureAirportLoading || arrivalAirportLoading) {
    return <FlightItemCardSkeleton />
  }

  if (!arrivalAirport || !departureAirport) {
    return null
  }

  return (
    <WithSelect>
      {showSelect && (
        <CircleCheckboxInput onClick={onClick} checked={!!selected} />
      )}
      <TripContext.Provider value={defaultTripContext}>
        <FlightCard
          className={cn({ selected: showSelect && !!selected })}
          item={flightItem}
          onClick={showSelect ? onClick : undefined}
        />
      </TripContext.Provider>
    </WithSelect>
  )
}

export default React.memo(FlightLookupTile)
